import { Loader2Icon } from "lucide-react";
import pluralize from "pluralize";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter } from "@/components/ui/dialog";
import useAnalytics from "@/hooks/use-analytics";
import useShare from "@/hooks/use-share";
import type { UserType } from "@/store";

import CopyNotification from "./copy-notification";
import { ModalCloseIcon } from "./icons";
import type { WrappedType } from "./use-wrapped";

interface ImageModalProps extends React.ComponentProps<typeof Dialog> {
  user: UserType;
  wrapped: WrappedType;
}

export default function ImageModal({
  user,
  wrapped,
  ...props
}: ImageModalProps) {
  const [isSharing, setIsSharing] = useState(false);

  const { track } = useAnalytics();
  const { share } = useShare({}, false);

  const imageUrl = useMemo(() => {
    const {
      badgesEarned = 0,
      citiesVisited = 0,
      escapeRoomsDone = 0,
      hauntsDone = 0,
      hoursSpent = 0,
    } = wrapped;

    const badgesEarnedLabel = encodeURIComponent(
      pluralize("badge", badgesEarned, true)
    );
    const citiesVisitedLabel = encodeURIComponent(
      pluralize("city", citiesVisited, true)
    );
    const escapeRoomsDoneLabel = encodeURIComponent(
      pluralize("escape room", escapeRoomsDone, true)
    );
    const hauntsDoneLabel = encodeURIComponent(
      pluralize("haunt", hauntsDone, true)
    );
    const hoursSpentLabel = encodeURIComponent(
      pluralize("hour", hoursSpent, true)
    );
    const username = encodeURIComponent(`@${user.username}`);

    if (escapeRoomsDone > 0 && hauntsDone > 0) {
      return `https://res.cloudinary.com/mortyapp/image/upload/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_110:2025,g_north_west,x_70,y_180,co_rgb:2AF5C4/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_110:My%20Immersive,g_north_west,x_70,y_300,co_rgb:ffffff/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_110:Recap,g_north_west,x_70,y_420,co_rgb:ffffff/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_85:${escapeRoomsDoneLabel},g_north_west,x_70,y_610,co_rgb:FFC850/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_85:${hauntsDoneLabel},g_north_west,x_70,y_700,co_rgb:FFC850/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_55:logged,g_north_west,x_70,y_800,co_rgb:ffffff/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_85:${hoursSpentLabel},g_north_west,x_70,y_930,co_rgb:FFC850/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_55:adventuring,g_north_west,x_70,y_1030,co_rgb:ffffff/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_85:${citiesVisitedLabel},g_north_west,x_70,y_1160,co_rgb:FFC850/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_55:visited,g_north_west,x_70,y_1260,co_rgb:ffffff/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_85:${badgesEarnedLabel},g_north_west,x_70,y_1390,co_rgb:FFC850/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_55:earned,g_north_west,x_70,y_1490,co_rgb:ffffff/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_36:Follow%20me%20on%20morty.app,g_north_west,x_190,y_1767,co_rgb:ffffff/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_36:${username},g_north_west,x_190,y_1816,co_rgb:ffffff/store/recap2023/recap1.png`;
    }
    if (hauntsDone > 0) {
      return `https://res.cloudinary.com/mortyapp/image/upload/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_110:2025,g_north_west,x_70,y_180,co_rgb:E92F7C/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_110:My%20Immersive,g_north_west,x_70,y_300,co_rgb:ffffff/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_110:Recap,g_north_west,x_70,y_420,co_rgb:ffffff/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_85:${hauntsDoneLabel},g_north_west,x_70,y_610,co_rgb:E92F7C/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_55:logged,g_north_west,x_70,y_710,co_rgb:ffffff/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_85:${hoursSpentLabel},g_north_west,x_70,y_840,co_rgb:E92F7C/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_55:adventuring,g_north_west,x_70,y_940,co_rgb:ffffff/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_85:${citiesVisitedLabel},g_north_west,x_70,y_1070,co_rgb:E92F7C/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_55:visited,g_north_west,x_70,y_1170,co_rgb:ffffff/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_85:${badgesEarnedLabel},g_north_west,x_70,y_1300,co_rgb:E92F7C/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_55:earned,g_north_west,x_70,y_1400,co_rgb:ffffff/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_36:Follow%20me%20on%20morty.app,g_north_west,x_190,y_1767,co_rgb:ffffff/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_36:${username},g_north_west,x_190,y_1816,co_rgb:ffffff/store/recap2023/recap3.png`;
    }

    return [
      `https://res.cloudinary.com/mortyapp/image/upload/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_110:2025,g_north_west,x_70,y_180,co_rgb:F3A21A/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_110:My%20Immersive,g_north_west,x_70,y_300,co_rgb:ffffff/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_110:Recap,g_north_west,x_70,y_420,co_rgb:ffffff/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_85:${escapeRoomsDoneLabel},g_north_west,x_70,y_610,co_rgb:F3A21A/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_55:logged,g_north_west,x_70,y_710,co_rgb:ffffff/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_85:${hoursSpentLabel},g_north_west,x_70,y_840,co_rgb:F3A21A/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_55:adventuring,g_north_west,x_70,y_940,co_rgb:ffffff/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_85:${citiesVisitedLabel},g_north_west,x_70,y_1070,co_rgb:F3A21A/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_55:visited,g_north_west,x_70,y_1170,co_rgb:ffffff/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_85:${badgesEarnedLabel},g_north_west,x_70,y_1300,co_rgb:F3A21A/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_55:earned,g_north_west,x_70,y_1400,co_rgb:ffffff/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_36:Follow%20me%20on%20morty.app,g_north_west,x_190,y_1767,co_rgb:ffffff/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_36:${username},g_north_west,x_190,y_1816,co_rgb:ffffff/store/recap2023/recap2.png`,
      `https://res.cloudinary.com/mortyapp/image/upload/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_110:2025,g_north_west,x_70,y_180,co_rgb:FFC850/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_110:My%20Immersive,g_north_west,x_70,y_300,co_rgb:ffffff/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_110:Recap,g_north_west,x_70,y_420,co_rgb:ffffff/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_85:${escapeRoomsDoneLabel},g_north_west,x_70,y_610,co_rgb:FFC850/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_55:logged,g_north_west,x_70,y_710,co_rgb:ffffff/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_85:${hoursSpentLabel},g_north_west,x_70,y_840,co_rgb:FFC850/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_55:adventuring,g_north_west,x_70,y_940,co_rgb:ffffff/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_85:${citiesVisitedLabel},g_north_west,x_70,y_1070,co_rgb:FFC850/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_55:visited,g_north_west,x_70,y_1170,co_rgb:ffffff/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_85:${badgesEarnedLabel},g_north_west,x_70,y_1300,co_rgb:FFC850/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_55:earned,g_north_west,x_70,y_1400,co_rgb:ffffff/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_36:Follow%20me%20on%20morty.app,g_north_west,x_190,y_1767,co_rgb:ffffff/l_text:fonts:thicccboi:okyuwk6bppmye7xuy6bo.ttf_36:${username},g_north_west,x_190,y_1816,co_rgb:ffffff/store/recap2023/recap4.png`,
    ][Math.floor(Math.random() * 2)];
  }, [wrapped, user.username]);

  const getImageBlog = async () => {
    const response = await fetch(imageUrl);
    return response.blob();
  };

  const trySharingImage = async () => {
    const blob = await getImageBlog();
    return share({
      files: [new File([blob], "recap.png", { type: blob.type })],
    });
  };

  const trySharingUrl = () => share({ url: imageUrl });

  const tryImageCopy = async () => {
    const blob = await getImageBlog();

    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          [blob.type]: blob,
        }),
      ]);
      return true;
    } catch {
      // Clipboard write not supported
    }

    return false;
  };

  const handleShare = async () => {
    setIsSharing(true);
    track("Immersive Recap 2025 Shared");

    if (!((await trySharingImage()) || trySharingUrl())) {
      if (!(await tryImageCopy())) {
        navigator.clipboard.writeText(imageUrl);
      }

      toast(<CopyNotification />);
    }

    setIsSharing(false);
  };

  return (
    <Dialog {...props}>
      <DialogContent
        className="overflow-hidden rounded-[15px] bg-[url(/images/main-bg.jpg)] bg-cover bg-top bg-no-repeat"
        overlayClassName="bg-gray-cool/64"
      >
        <Button
          aria-label="Close"
          className="absolute top-5 right-5 size-[25px]"
          onClick={() => props.onOpenChange?.(false)}
        >
          <ModalCloseIcon />
        </Button>
        <img alt="My Immersive Recap" src={imageUrl} />
        <DialogFooter className="p-5">
          <Button
            className="h-[45px] w-full rounded-[23px]"
            disabled={isSharing}
            onClick={handleShare}
            variant="cta"
          >
            {isSharing ? <Loader2Icon className="animate-spin" /> : "Share"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
